/**
 * <campaign-timer>: countdown to the campaign end date (theme settings) or to the next midnight.
 *
 * SWATI (perf, 2026-09-26): the countdown used to replace its whole innerHTML every second and
 * showed nothing for the first second.
 * - Replacing elements every second made Chrome re-run :has() invalidation for <body> and <html>
 *   on each tick (v4 anchors several `body:has(...)` / `html:has(...)` rules there): ~200 elements
 *   restyled per second for as long as the page is open. Only the digits change, so the markup is
 *   built once and each tick rewrites the digit text nodes that changed: no element is inserted or
 *   removed, so no :has() invalidation and nothing for DOM-mutation observers to re-scan.
 * - The first paint happened one interval (1s) after upgrade, so the announcement bar / promotion
 *   block grew by a line after first paint and pushed the page down (home CLS 0.063). It now renders
 *   as soon as the element is connected. The rendered markup is unchanged.
 */
class CampaignTimer extends HTMLElement {
  connectedCallback() {
    if (this.intervalId) return;

    const useCustom = this.dataset.useCustom === 'true';
    const isoDate = this.dataset.iso;
    const hideDays = this.dataset.hideDays === 'true';
    const shop = (window.Shopify && window.Shopify.shop) || '';
    const offset = (this.dataset.timezone || '').trim();
    const template = (this.dataset.template || 'html').toLowerCase();
    this.template = template;

    this.hideDays = hideDays;

    if (useCustom && isoDate) {
      this.endDate = new Date(isoDate).getTime();
    } else {
      const today = new Date();
      const next = new Date(today);
      next.setDate(today.getDate() + 1);
      // Default to +02:00, allow override via data-timezone, special case for India shop
      let timezone = offset || '+02:00';
      if (!offset && shop === 'swati-india.myshopify.com') timezone = '+05:30';

      const dd = String(next.getDate()).padStart(2, '0');
      const mm = String(next.getMonth() + 1).padStart(2, '0');
      const yyyy = next.getFullYear();
      const nextISO = `${yyyy}-${mm}-${dd}T00:00:00${timezone}`;
      this.endDate = new Date(nextISO).getTime();
    }

    this.labels = {
      day: this.dataset.tDay || 'Day',
      hours: this.dataset.tHours || 'Hours',
      minutes: this.dataset.tMinutes || 'Min',
      seconds: this.dataset.tSeconds || 'Sec',
      endingIn: this.dataset.tEndingIn || 'Ending in:',
    };

    /** @type {Text[] | null} digit text nodes, in render order */
    this.digitNodes = null;

    this.init();
  }

  disconnectedCallback() {
    clearInterval(this.intervalId);
    this.intervalId = 0;
  }

  /**
   * @param {number} n
   * @returns {string}
   */
  toTwoDigits(n) {
    return n < 10 ? `0${n}` : String(n);
  }

  /**
   * @param {number} days
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   * @returns {string}
   */
  renderHTML(days, hours, minutes, seconds) {
    return `
      <div class="campaign-timer__inner">
        ${
          this.hideDays
            ? ''
            : `<div class="campaign-timer__unit"><span>${this.toTwoDigits(days)}</span><strong>${this.labels.day}</strong></div><span class="campaign-timer__sep">:</span>`
        }
        <div class="campaign-timer__unit"><span>${this.toTwoDigits(hours)}</span><strong>${this.labels.hours}</strong></div><span class="campaign-timer__sep">:</span>
        <div class="campaign-timer__unit"><span>${this.toTwoDigits(minutes)}</span><strong>${this.labels.minutes}</strong></div><span class="campaign-timer__sep">:</span>
        <div class="campaign-timer__unit"><span>${this.toTwoDigits(seconds)}</span><strong>${this.labels.seconds}</strong></div>
      </div>
    `;
  }

  /**
   * @param {number} days
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   * @returns {string}
   */
  renderText(days, hours, minutes, seconds) {
    return `${this.toTwoDigits(hours)}:${this.toTwoDigits(minutes)}:${this.toTwoDigits(seconds)}`;
  }

  /**
   * Writes the current remaining time. The first call builds the markup; later calls only change
   * the text of the digits that differ.
   * @param {number} d milliseconds left
   */
  update(d) {
    const days = Math.floor(d / (1000 * 60 * 60 * 24));
    const hours = Math.floor((d % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((d % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((d % (1000 * 60)) / 1000);

    if (this.template === 'text') {
      const value = this.renderText(days, hours, minutes, seconds);
      const node = this.firstChild;
      if (node instanceof Text && node === this.lastChild) {
        if (node.data !== value) node.data = value;
      } else {
        this.textContent = value;
      }
      return;
    }

    const values = this.hideDays ? [hours, minutes, seconds] : [days, hours, minutes, seconds];
    const nodes = this.digitNodes;
    const inner = this.firstElementChild;

    // Reuse the digits only while they are still ours: a section re-render (morph) can swap in the
    // server placeholder (snippets/swati-announcement-timer), which must be rebuilt, not filled.
    if (
      nodes &&
      nodes.length === values.length &&
      nodes.every((node) => node.isConnected && this.contains(node)) &&
      inner &&
      !inner.classList.contains('campaign-timer__inner--placeholder')
    ) {
      values.forEach((value, index) => {
        const text = this.toTwoDigits(value);
        const node = /** @type {Text} */ (nodes[index]);
        if (node.data !== text) node.data = text;
      });
      return;
    }

    this.innerHTML = this.renderHTML(days, hours, minutes, seconds);
    this.digitNodes = Array.from(this.querySelectorAll('.campaign-timer__unit > span'), (span) =>
      span.firstChild instanceof Text ? span.firstChild : span.appendChild(document.createTextNode(''))
    );
  }

  expire() {
    clearInterval(this.intervalId);
    this.intervalId = 0;
    this.digitNodes = null;
    this.innerHTML = '';
    // SWATI (QA C-2): let CSS hide the surrounding label/bar too, not just the digits.
    this.setAttribute('expired', '');
  }

  init() {
    const distance = () => this.endDate - Date.now();
    if (this.endDate && distance() > 0) {
      this.update(distance());
      this.intervalId = setInterval(() => {
        const d = distance();
        this.update(d);

        if (distance() <= 0) {
          this.expire();
          try {
            localStorage.removeItem('fbPromoCode');
            localStorage.removeItem('fbPromoEndTime');
          } catch (e) {}
        }
      }, 1000);
    } else {
      // SWATI (QA C-2): already over when the page loaded.
      this.expire();
    }
  }
}

if (!customElements.get('campaign-timer')) customElements.define('campaign-timer', CampaignTimer);
